import { BellOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    getAbbreviatedTimezoneName,
    TimezoneKey,
} from "@impero-com/common/utils/timezone";
import { IconTimeline } from "@impero-com/ui";
import {
    format,
    getHours,
    getMilliseconds,
    getMinutes,
    getSeconds,
    setHours,
    setMilliseconds,
    setMinutes,
    setSeconds,
    subDays,
    subHours,
} from "date-fns";
import { useWatch, Control } from "react-hook-form";

import { TestingControlFormSchema } from "../testingControlSchema";

interface TestingControlTimelineProps {
    control: Control<TestingControlFormSchema>;
}
export function TestingControlTimeline({
    control,
}: TestingControlTimelineProps) {
    const { t } = useTranslation();
    const dueDate = useWatch({ control, name: "dueDate" });
    const responsibleNotificationTime = useWatch({
        control,
        name: "notificationTime",
    });
    const responsibleAllocatedTime = useWatch({
        control,
        name: "responsible.allocatedTime",
    });
    const reviewers = useWatch({
        control,
        name: "reviewers",
    });

    const timezoneName = useWatch({
        control,
        name: "timezoneName",
    });

    const items = [
        {
            date: formatLocalDateWithTimezone(
                new Date(
                    calculateSendTime(
                        dueDate,
                        responsibleNotificationTime,
                        responsibleAllocatedTime,
                        reviewers
                    )
                ),
                timezoneName
            ),
            label: t("TestingControlView_TimelineSendTime"),
            icon: <MailOutlined />,
        },
        {
            date: formatLocalDateWithTimezone(
                calculateResponsibleDueDate(dueDate, reviewers),
                timezoneName
            ),
            label: t("TestingControlView_ResponsibleDueDate"),
            icon: <BellOutlined />,
        },
    ];
    if (reviewers.reviewer?.userId) {
        items.push({
            date: formatLocalDateWithTimezone(
                calculateAnyReviewerDueDate(dueDate, reviewers.secondReviewer),
                timezoneName
            ),
            label: t("TestingControlView_ReviewDueDate"),
            icon: <BellOutlined />,
        });
    }
    if (reviewers.secondReviewer?.userId && reviewers.reviewer?.userId) {
        items.push({
            date: formatLocalDateWithTimezone(dueDate, timezoneName),
            label: t("TestingControlView_SecondReviewDueDate"),
            icon: <BellOutlined />,
        });
    }
    return <IconTimeline items={items} />;
}

function calculateResponsibleStartDate(
    dueDate: Date,
    responsibleAllocatedTime: { unit: "Days" | "Hours"; value: number },
    reviewers: TestingControlFormSchema["reviewers"]
): Date {
    const responsibleDueDate = calculateResponsibleDueDate(dueDate, reviewers);
    if (responsibleAllocatedTime.unit === "Days") {
        return subDays(responsibleDueDate, responsibleAllocatedTime.value);
    } else {
        return subHours(responsibleDueDate, responsibleAllocatedTime.value);
    }
}

export function calculateSendTime(
    dueDate: Date,
    responsibleNotificationTime: Date,
    responsibleAllocatedTime: { unit: "Days"; value: number },
    reviewers: TestingControlFormSchema["reviewers"]
) {
    const responsibleStartDate = calculateResponsibleStartDate(
        dueDate,
        responsibleAllocatedTime,
        reviewers
    );
    let sendTime = new Date(responsibleStartDate);
    sendTime = setHours(sendTime, getHours(responsibleNotificationTime));
    sendTime = setMinutes(sendTime, getMinutes(responsibleNotificationTime));
    sendTime = setSeconds(sendTime, getSeconds(responsibleNotificationTime));
    sendTime = setMilliseconds(
        sendTime,
        getMilliseconds(responsibleNotificationTime)
    );

    if (sendTime > dueDate) {
        sendTime = subDays(sendTime, 1);
    }

    return sendTime;
}

export function calculateAnyReviewerDueDate(
    dueDate: Date,
    reviewer: TestingControlFormSchema["reviewers"]["reviewer"]
): Date {
    if (!reviewer?.userId || !reviewer?.allocatedTime) {
        return dueDate;
    }
    const reviewerAllocatedTime = reviewer.allocatedTime;
    switch (reviewerAllocatedTime.unit) {
        case "Days": {
            return subDays(dueDate, reviewerAllocatedTime.value);
        }
        case "Hours": {
            return subHours(dueDate, reviewerAllocatedTime.value);
        }
        default: {
            return assertNever(reviewerAllocatedTime);
        }
    }
}

export function calculateResponsibleDueDate(
    dueDate: Date,
    reviewers: TestingControlFormSchema["reviewers"]
): Date {
    if (!reviewers.reviewer?.userId) {
        return dueDate;
    }

    let result = dueDate;

    // Calculate allocated time for first reviewer
    if (reviewers.reviewer?.allocatedTime) {
        result = calculateAnyReviewerDueDate(result, reviewers.reviewer);
    }

    // Calculate allocated time for second reviewer
    if (reviewers.reviewer && reviewers.secondReviewer?.allocatedTime) {
        result = calculateAnyReviewerDueDate(result, reviewers.secondReviewer);
    }

    return result;
}

export function formatLocalDateWithTimezone(
    date: Date,
    timezoneName: TimezoneKey
): string {
    const formattedDate = format(
        date,
        `dd-LL-yyyy HH:mm '${getAbbreviatedTimezoneName(timezoneName)}'`
    );
    return formattedDate;
}
